import { AlertDialog } from '@astryxdesign/core/AlertDialog';
import { Banner } from '@astryxdesign/core/Banner';
import { Button } from '@astryxdesign/core/Button';
import { Card } from '@astryxdesign/core/Card';
import { Heading } from '@astryxdesign/core/Heading';
import { HStack } from '@astryxdesign/core/HStack';
import { Link } from '@astryxdesign/core/Link';
import { MetadataList, MetadataListItem } from '@astryxdesign/core/MetadataList';
import { StatusDot } from '@astryxdesign/core/StatusDot';
import { Text } from '@astryxdesign/core/Text';
import { useToast } from '@astryxdesign/core/Toast';
import { VStack } from '@astryxdesign/core/VStack';
import { ArrowLeft, Key, ShareNetwork } from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router';
import { useCancelMyBooking, useMyBooking } from '@/api/public';
import { EmptyView, QueryView } from '@/components/StateViews';
import { useStudioData } from '@/features/studio/StudioContext';
import { errorText } from '@/lib/errors';
import { fmtDateTime, fmtDayLong, fmtDuration, fmtMoney, fmtTime } from '@/lib/format';
import { bookingLink, findBooking, saveBooking } from '@/lib/myBookings';
import { ReminderCard } from './ReminderCard';
import { STATUS_DOT, STATUS_TEXT } from './status';

/** Token from this device's keychain, or from a shared link's #t= fragment. */
function useBookingToken(slug: string, id: string): string | undefined {
  const { hash } = useLocation();
  return useMemo(() => {
    const fromHash = new URLSearchParams(hash.slice(1)).get('t') ?? undefined;
    return fromHash ?? findBooking(slug, id)?.token;
  }, [hash, slug, id]);
}

export function BookingDetailsPage() {
  const studio = useStudioData();
  const { bookingId: fromPath } = useParams();
  const [params] = useSearchParams();
  const bookingId = fromPath ?? params.get('id') ?? '';
  const token = useBookingToken(studio.slug, bookingId);
  const q = useMyBooking(studio.slug, bookingId, token);
  const cancel = useCancelMyBooking(studio.slug, bookingId, token ?? '');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const showToast = useToast();
  const justCreated = params.get('created') === '1';
  const tz = studio.timezone;

  // A booking opened from a shared link is remembered on this device too.
  useEffect(() => {
    if (q.data && token && !findBooking(studio.slug, bookingId)) {
      saveBooking(studio.slug, { id: q.data.id, token, code: q.data.code, startsAt: q.data.startsAt, serviceName: q.data.serviceName });
      if (location.hash) history.replaceState(history.state, '', location.pathname + location.search);
    }
  }, [q.data, token, studio.slug, bookingId]);

  if (!token) {
    return (
      <VStack paddingInline={4} paddingBlock={6} className="page-column">
        <EmptyView
          icon={<Key size={32} />}
          title="Нет доступа к этой записи"
          description="Откройте запись с того устройства, где записывались, или по ссылке из подтверждения."
          action={<Button label="Мои записи" href={`/s/${studio.slug}/my`} />}
        />
      </VStack>
    );
  }

  const share = async () => {
    const url = bookingLink(studio.slug, bookingId, token);
    try {
      if (navigator.share) await navigator.share({ title: studio.settings.name, text: 'Моя запись', url });
      else {
        await navigator.clipboard.writeText(url);
        showToast({ body: 'Ссылка скопирована. Не пересылайте её посторонним: по ней можно отменить запись.' });
      }
    } catch {
      /* share sheet dismissed */
    }
  };

  return (
    <VStack gap={5} paddingInline={4} paddingBlock={6} className="page-column">
      <HStack gap={2} vAlign="center">
        <Button label="Мои записи" variant="ghost" icon={<ArrowLeft size={18} />} href={`/s/${studio.slug}/my`} />
      </HStack>

      <QueryView query={q}>
        {(b) => (
          <VStack gap={5}>
            {justCreated && b.status === 'confirmed' && (
              <Banner
                status="success"
                title="Вы записаны!"
                description={`${fmtDayLong(b.startsAt, tz)}, ${fmtTime(b.startsAt, tz)}. Ждём вас.`}
                collapsible={false}
              />
            )}
            <VStack gap={2}>
              <HStack gap={2} vAlign="center">
                <StatusDot variant={STATUS_DOT[b.status]} label={STATUS_TEXT[b.status]} />
                <Text weight="semibold">{STATUS_TEXT[b.status]}</Text>
                {b.isDemo && <Text type="supporting">· демо</Text>}
              </HStack>
              <Heading level={1}>{b.serviceName}</Heading>
              <Text type="large">
                {fmtDayLong(b.startsAt, tz)}, {fmtTime(b.startsAt, tz)}
              </Text>
            </VStack>

            <Card>
              <MetadataList>
                <MetadataListItem label="Код записи">
                  <Text weight="semibold" hasTabularNumbers>
                    {b.code}
                  </Text>
                </MetadataListItem>
                <MetadataListItem label="Длительность">{fmtDuration(b.durationMin)}</MetadataListItem>
                {b.durationMin >= 720 && <MetadataListItem label="Готово примерно">{fmtDateTime(b.endsAt, tz)}</MetadataListItem>}
                <MetadataListItem label="Стоимость">{fmtMoney(b.priceCents, b.currency, b.priceIsFrom)}</MetadataListItem>
                {b.resourceName && <MetadataListItem label="Пост">{b.resourceName}</MetadataListItem>}
                <MetadataListItem label="Автомобиль">{b.carInfo}</MetadataListItem>
                <MetadataListItem label="Адрес">
                  {studio.settings.mapUrl ? (
                    <Link href={studio.settings.mapUrl} isExternalLink>
                      {studio.settings.address}
                    </Link>
                  ) : (
                    studio.settings.address
                  )}
                </MetadataListItem>
              </MetadataList>
            </Card>

            {b.status === 'confirmed' && <ReminderCard studio={studio} booking={b} token={token} />}

            <Button label="Ссылка на запись для другого устройства" icon={<ShareNetwork size={18} />} variant="ghost" onClick={share} />

            {b.status === 'confirmed' &&
              (b.canCancel ? (
                <Button label="Отменить запись" variant="destructive" onClick={() => setConfirmOpen(true)} />
              ) : (
                <Text type="supporting">
                  Отменить онлайн можно не позднее чем за {b.cancelCutoffHours} ч. Позвоните: {studio.settings.phone}
                </Text>
              ))}
            {cancel.error && <Banner status="error" title={errorText(cancel.error)} collapsible={false} />}

            <AlertDialog
              isOpen={confirmOpen}
              onOpenChange={setConfirmOpen}
              title="Отменить запись?"
              description={`${b.serviceName}, ${fmtDateTime(b.startsAt, tz)}. Время освободится для других клиентов.`}
              actionLabel="Отменить запись"
              cancelLabel="Не отменять"
              isActionLoading={cancel.isPending}
              onAction={() =>
                cancel.mutate('', {
                  onSuccess: () => {
                    setConfirmOpen(false);
                    showToast({ body: 'Запись отменена' });
                  },
                  onError: () => setConfirmOpen(false),
                })
              }
            />
          </VStack>
        )}
      </QueryView>
    </VStack>
  );
}
